import Api from '@sendsprint/api-types';
import { CONFIG_NO_ERROR_TOAST, DURATION_10_MIN } from '@src/constants';
import {
  addWhiteLabelMetaDataService,
  domainNameCheckService,
  getWhiteLabelConfigurationService,
  publishWhiteLabelService,
  saveDomainSettingsService,
  saveMediaFilesService,
  saveSmtpSettingsService,
  saveSocialLinksService,
  saveStylingConfigurationService
} from '@src/services/white-label-service';
import ClientApi from '@src/types/client';
import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from 'react-query';

export const useGetWhiteLabelConfiguration = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<
    ClientApi.WhiteLabel.GetWhiteLabelConfigurations.Response,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>
  >
) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return useQuery<ClientApi.WhiteLabel.GetWhiteLabelConfigurations.Response, AxiosError<any>>({
    queryKey: ['getWhiteLabelConfiguration'],
    queryFn: () => getWhiteLabelConfigurationService(CONFIG_NO_ERROR_TOAST),
    staleTime: DURATION_10_MIN,
    ...options
  });
};

export const useSaveMediaFiles = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.SaveMediaFiles.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.SaveMediaFiles.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.SaveMediaFiles.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.SaveMediaFiles.Request
  >('saveMediaFiles', saveMediaFilesService, options);
};

export const useSaveStylingConfiguration = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.SaveStylingConfiguration.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.SaveStylingConfiguration.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.SaveStylingConfiguration.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.SaveStylingConfiguration.Request
  >('saveStylingConfiguration', saveStylingConfigurationService, options);
};

export const useSaveSocialLinks = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.SaveSocialLinks.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.SaveSocialLinks.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.SaveSocialLinks.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.SaveSocialLinks.Request
  >('saveSocialLinks', saveSocialLinksService, options);
};

export const useSaveSmtpSettings = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.SaveSmtpSettings.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.SaveSmtpSettings.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.SaveSmtpSettings.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.SaveSmtpSettings.Request
  >('saveSmtpSettings', saveSmtpSettingsService, options);
};

export const useSaveDomainSettings = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.SaveDomainSettings.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.SaveDomainSettings.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.SaveDomainSettings.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.SaveDomainSettings.Request
  >('saveDomainSettings', saveDomainSettingsService, options);
};

export const useDomainNameCheck = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.DomainNameCheck.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.DomainNameCheck.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.DomainNameCheck.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    ClientApi.WhiteLabel.DomainNameCheck.Request
  >('domainNameCheck', domainNameCheckService, options);
};

export const usePublishWhiteLabel = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.PublishWhiteLabel.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.PublishWhiteLabel.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >('publishWhiteLabel', publishWhiteLabelService, options);
};

export const useAddWhiteLabelMetadata = (
  options?: Omit<
    UseMutationOptions<
      ClientApi.WhiteLabel.AddMetaData.Response,
      AxiosError<Api.Endpoint.Response.ErrorResponse>,
      ClientApi.WhiteLabel.AddMetaData.Request
    >,
    'mutationKey' | 'mutationFn'
  >
) => {
  return useMutation<
    ClientApi.WhiteLabel.AddMetaData.Response,
    AxiosError<Api.Endpoint.Response.ErrorResponse>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ClientApi.WhiteLabel.AddMetaData.Request
  >('addMetaData', addWhiteLabelMetaDataService, options);
};
